import { useQuery } from "@apollo/client";
import { Box, Grid, LinearProgress, Stack, TextField } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { gql } from "../../../__generated__";
import {
  AvailabilityType,
  Currency,
  Iso639,
} from "../../../__generated__/graphql";
import CitySelector from "../../../components/CitySelector";
import CountrySelector from "../../../components/CountrySelector";
import TagsSelector from "../../../components/TagsSelector";
import TourTypeSelector, {
  TourTypeFilter,
} from "../../../components/TourTypeSelector";
import GlobalContext from "../../../global-context";
import LatestNews from "./LatestNews";
import NewTours from "./NewTours";
import TourCard from "./TourCard";

export default function ToursList() {
  const globalContext = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState<string | null>(getDefault("search"));
  const [countryId, setCountryId] = useState(getDefaultCountry());
  const [cityId, setCityId] = useState(getDefault("locationId"));
  const [tourType, setTourType] =
    useState<TourTypeFilter>(getDefaultTourType());
  const [tagIds, setTagIds] = useState<string[]>([]);

  useEffect(updateSearchParams, [
    search,
    countryId,
    cityId,
    tourType,
    setSearchParams,
  ]);

  const { data, loading, error } = useQuery(GET_PRODUCTS, {
    variables: {
      input: {
        locationId: cityId,
        countryId,
        search,
        availabilityTypes: getAvailabilityTypes(tourType),
        tagIds: tagIds.length ? tagIds : undefined,
      },
      lang: Iso639.En,
      currency: globalContext.currency as Currency,
    },
  });
  if (error) return <>ERROR</>;

  const products = data?.products.records || [];

  return (
    <>
      <LatestNews />
      <NewTours />
      <Box
        sx={{ fontSize: "18px", fontWeight: "bold", mt: "20px", mb: "20px" }}
      >
        All Tours
      </Box>
      <Stack sx={{ mb: "20px" }} direction="row" spacing={2}>
        <TextField
          sx={{ backgroundColor: "white", width: "250px" }}
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <CountrySelector
          sx={{ backgroundColor: "white" }}
          country={countryId}
          setCountry={(countryId) => {
            setCityId("");
            setCountryId(countryId);
          }}
        />
        <CitySelector countryId={countryId} city={cityId} setCity={setCityId} />
        <TagsSelector tagIds={tagIds} setTagIds={setTagIds} />
        <TourTypeSelector tourType={tourType} setTourType={setTourType} />
      </Stack>
      {loading && <LinearProgress color="error" />}
      {!loading && (
        <Grid container spacing={3}>
          {products.map((product) => (
            <Grid key={product.id} item xl={3} md={4} sm={6} xs={12}>
              <TourCard product={product} />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );

  function getDefault(name: string) {
    const value = searchParams.get(name);
    return value ? value : "";
  }

  function getDefaultTourType(): TourTypeFilter {
    const value =
      searchParams.get("privateOption") ||
      searchParams.get("hasPrivateOption") ||
      "";

    if (value === "privateAvailable" || value === "yes" || value === "true")
      return "privateAvailable";
    return "allTours";
  }

  function getDefaultCountry() {
    const countryId = searchParams.get("countryId");
    const localStorageUser = JSON.parse(localStorage.getItem("user") || "");

    if (countryId) return countryId;
    if (localStorageUser) return localStorageUser.countryId || "";
    return "";
  }

  function updateSearchParams() {
    const searchParams: Record<string, string> = {};
    if (search) searchParams.search = search;
    if (countryId) searchParams.countryId = countryId;
    if (cityId) searchParams.cityId = cityId;
    if (tourType === "privateAvailable") searchParams.privateOption = tourType;
    setSearchParams(searchParams);
  }
}

function getAvailabilityTypes(tourType: TourTypeFilter) {
  if (tourType === "privateAvailable")
    return [AvailabilityType.Private, AvailabilityType.PublicAndPrivate];
  return undefined;
}

const GET_PRODUCTS = gql(`
  query GetProducts(
    $input: ProductsInput
    $lang: Iso639!
    $currency: Currency!
  ) {
    products(input: $input) {
      totalRecords
      records {
        id
        duration
        mainImage
        availabilityType
        translation(lang: $lang) {
          name
          metaDescription
        }
        location {
          translation(lang: $lang) {
            name
          }
        }
        mainPrice(currency: $currency) {
          amount
          currency
          isGroup
        }
      }
    }
  }
`);
